import { Component, ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
  tags?: Record<string, string>;
  level?: 'page' | 'component' | 'widget';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Reusable Error Boundary component that lazily loads Sentry only when an error occurs.
 * This keeps Sentry out of the critical rendering path.
 */
export class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { context, tags, level = 'component' } = this.props;

    // Dynamically import Sentry only when an error actually occurs
    import('@sentry/react').then((Sentry) => {
      Sentry.withScope((scope) => {
        scope.setTag('error_boundary', level);
        if (context) {
          scope.setTag('component_context', context);
          scope.setContext('Component Context', { name: context });
        }
        
        if (tags) {
          Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });
        }

        scope.setContext('React Error Info', {
          componentStack: errorInfo.componentStack,
        });

        Sentry.captureException(error);
      });
    }).catch(() => {
      console.error('[SentryErrorBoundary] Failed to load Sentry:', error);
    });

    this.setState({ error, errorInfo });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          componentStack={this.state.errorInfo?.componentStack || ''}
          eventId=""
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap components with error boundaries
 */
export function withSentryErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  boundaryProps?: Omit<Props, 'children'>
) {
  return function WithErrorBoundary(props: P) {
    return (
      <SentryErrorBoundary {...boundaryProps}>
        <Component {...props} />
      </SentryErrorBoundary>
    );
  };
}
